import { useState, useMemo } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { OdinButton } from '@/components/ui/odin-button'
import { EditIcon } from '@/components/ui/icons/EditIcon'
import { X, Store } from 'lucide-react'
import InlineQuantityInput from './InlineQuantityInput'
import { InlineSelectField } from '@/components/ui/inline-select-field'
import ConfirmButton from './ConfirmButton'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

const ADJUSTABLE_STATUSES = ['packed', 'delivered', 'collected', 'sent', 'invoiced', 'paid']

// Growing material names are stored as "Strain – Format" (e.g. "Shiitake – Shiitake Log").
// When we render the strain in the primary column, strip it from the format label
// shown in the wide cell to avoid duplication ("Shiitake | 2 x Shiitake Log").
function stripStrainPrefix(name, strain) {
  if (!name) return ''
  if (!strain) return name
  const escaped = strain.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const stripped = name.replace(new RegExp(`^${escaped}\\s*[\\u2013\\u2014-]\\s*`, 'i'), '')
  return stripped || name
}

// Status options for sales - matching old SalesRecord
const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', variant: 'status-pending' },
  { value: 'packed', label: 'Packed', variant: 'status-packed' },
  { value: 'delivered', label: 'Delivered', variant: 'status-delivered' },
  { value: 'collected', label: 'Collected', variant: 'status-collected' },
  { value: 'sent', label: 'Sent', variant: 'status-sent' },
  { value: 'invoiced', label: 'Invoiced', variant: 'status-invoiced' },
  { value: 'paid', label: 'Paid', variant: 'status-paid' }
]

// Market sales: limited status lifecycle (no packed/sent/collected/invoiced)
const MARKET_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', variant: 'status-pending' },
  { value: 'delivered', label: 'Delivered', variant: 'status-delivered' },
  { value: 'paid', label: 'Paid', variant: 'status-paid' }
]

/**
 * TimelineRow - Main timeline row component handling confirmed and unconfirmed sales
 *
 * Renders sales records with conditional styling based on type:
 * - unconfirmed-suggestion: Light slate background (70%), inline editable, AI badge, confirm button
 * - unconfirmed-recurring: Light amber background (70%), inline editable, Rec badge, "Pending" consignment
 * - committed-recurring: White background, inline editable, normal display
 * - confirmed: Background based on status (delivered=green, invoiced, paid), status dropdown
 *
 * @param {Object} props
 * @param {Object} props.item - Timeline item data
 * @param {string} props.item.id - Unique item ID
 * @param {string} props.item.type - Item type ('confirmed', 'unconfirmed-recurring', 'unconfirmed-suggestion', 'committed-recurring')
 * @param {string} props.item.sourceId - Original source ID
 * @param {string} props.item.date - ISO date string
 * @param {Object} props.item.customer - { id, name }
 * @param {Object} props.item.branch - { id, name }
 * @param {Array} props.item.items - Array of { productId, productTypeId, strain, productTypeName, quantity, price, subtotal }
 * @param {number} props.item.total - Total amount
 * @param {string|null} props.item.status - Status ('pending', 'packed', 'delivered', 'invoiced', 'paid')
 * @param {string|null} props.item.consignmentNo - Consignment number
 * @param {string|null} props.item.invoiceNo - Invoice number (for invoiced/paid items)
 * @param {string|null} props.item.recurringOrderId - ID of associated recurring order
 * @param {Function} props.onConfirm - Called when confirm button clicked: (item, editedQuantities) => void
 * @param {Function} props.onDismiss - Called when dismiss button clicked: (item) => void
 * @param {Function} props.onQuantityChange - Called when quantity edited: (itemId, productId, quantity) => void
 * @param {Function} props.onStatusChange - Called when status changed: (itemId, newStatus) => void
 * @param {Function} props.onEdit - Called when edit button clicked: (item) => void
 * @param {Function} props.onEditRecurringOrder - Called when edit recurring order button clicked: (item) => void
 * @param {Function} props.onViewInvoice - Called when invoice button clicked: (item) => void
 * @param {boolean} props.isConfirming - Whether this row is currently being confirmed (shows loading/animation)
 * @param {string} props.className - Additional CSS classes
 */
export default function TimelineRow({
  item,
  onConfirm,
  onDismiss,
  onQuantityChange,
  onStatusChange,
  onEdit,
  onEditRecurringOrder,
  onViewInvoice,
  onCustomerClick,
  onAdjustmentChange,
  onAcceptAdjustments,
  productTypes = [],
  isConfirming = false,
  className
}) {
  const isUnconfirmed = item.type === 'unconfirmed-suggestion' || item.type === 'unconfirmed-recurring'
  const isConfirmed = item.type === 'confirmed' || item.type === 'committed-recurring'
  const isAdjustable = isConfirmed && ADJUSTABLE_STATUSES.includes(item.status)
  const hasInvoice = (item.status === 'invoiced' || item.status === 'paid') && item.invoiceNo
  const isInvoicedOrPaid = item.status === 'invoiced' || item.status === 'paid'

  // Batch pending changes for invoiced/paid rows
  const [pendingChanges, setPendingChanges] = useState({})
  const hasPendingChanges = Object.keys(pendingChanges).length > 0

  // Calculate estimated credit from pending changes
  const estimatedCredit = useMemo(() => {
    if (!hasPendingChanges) return 0
    return Object.entries(pendingChanges).reduce((total, [saleItemId, changes]) => {
      const lineItem = item.items.find(li => li.id === saleItemId)
      if (!lineItem) return total
      const origSubtotal = lineItem.subtotal
      const newQuantity = changes.quantity ?? lineItem.quantity
      const newPrice = lineItem.price // Price may change with class, but we use current for estimate
      const newSubtotal = newQuantity * newPrice
      return total + (origSubtotal - newSubtotal)
    }, 0)
  }, [hasPendingChanges, pendingChanges, item.items])

  // Determine row background based on type and status
  const getRowBackground = () => {
    if (isConfirming) {
      return 'timeline-row--confirming' // Animation class for green flash
    }

    switch (item.type) {
      case 'unconfirmed-suggestion':
        return 'bg-slate-100/70'
      case 'unconfirmed-recurring':
        return 'bg-amber-50/70'
      case 'committed-recurring':
      case 'confirmed':
        // Market sales: pending=white, delivered=green, paid=grey (no invoiced/purple)
        if (item.marketEventId) {
          switch (item.status) {
            case 'delivered':
            case 'collected':
            case 'sent':
              return 'delivered'
            case 'paid':
              return 'paid'
            default:
              return 'bg-white'
          }
        }
        // Standard status-based styling for non-market items
        switch (item.status) {
          case 'delivered':
          case 'collected':
          case 'sent':
            return 'delivered'
          case 'invoiced':
            return 'invoiced'
          case 'paid':
            return 'paid'
          default:
            return 'bg-white'
        }
      default:
        return 'bg-white'
    }
  }

  // Determine consignment display
  const getConsignmentDisplay = () => {
    if (item.type === 'unconfirmed-suggestion') {
      return <span className="text-muted-foreground italic text-sm">—</span>
    }
    if (item.type === 'unconfirmed-recurring') {
      return (
        <span className="text-muted-foreground italic text-sm">
          Pending
        </span>
      )
    }
    // Confirmed or committed-recurring show actual consignment
    return item.consignmentNo ? (
      <span className="font-semibold">
        {item.consignmentNo}
      </span>
    ) : (
      <span className="text-muted-foreground italic text-sm">Pending</span>
    )
  }


  // Status select dropdown (only for confirmed items)
  // Invoiced/paid sales show a static badge (not interactive) to prevent status regression
  const renderStatusSelect = () => {
    if (!isConfirmed) return null

    const currentStatus = item.status || 'pending'
    const isMarket = !!item.marketEventId
    const options = isMarket ? MARKET_STATUS_OPTIONS : STATUS_OPTIONS
    const selectedStatus = options.find(opt => opt.value === currentStatus) || options[0]

    // Invoiced/paid: render static badge (no dropdown) — status is locked
    if (currentStatus === 'invoiced' || currentStatus === 'paid') {
      return (
        <div className={cn('w-32 h-9 flex items-center justify-center rounded-md text-sm font-medium', selectedStatus.variant)}>
          {selectedStatus.label}
        </div>
      )
    }

    // Market sales: only show market-specific statuses
    // Standard sales: filter out 'invoiced' and 'paid' (set programmatically only)
    const allowedOptions = isMarket
      ? MARKET_STATUS_OPTIONS
      : STATUS_OPTIONS.filter(opt => opt.value !== 'invoiced' && opt.value !== 'paid')

    return (
      <Select
        value={currentStatus}
        onValueChange={(newStatus) => onStatusChange?.(item.id, newStatus)}
      >
        <SelectTrigger className={cn('w-32 border-0', selectedStatus.variant)}>
          <SelectValue>{selectedStatus.label}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {allowedOptions.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              className="cursor-pointer"
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  // Action button - either Edit or View Invoice
  const renderActionButton = () => {
    if (!isConfirmed) return null

    // Show invoice button if invoiced/paid with invoice number
    if (hasInvoice) {
      return (
        <OdinButton
          variant="table-action"
          size="small"
          onClick={() => onViewInvoice?.(item)}
          title="View invoice"
        >
          {item.invoiceNo}
        </OdinButton>
      )
    }

    // Show edit button for other confirmed items
    return (
      <OdinButton
        variant="table-action"
        size="small"
        leftIcon={<EditIcon />}
        onClick={() => onEdit?.(item)}
        title="Edit sale"
      >
        Edit
      </OdinButton>
    )
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount)
  }

  const handleQuantityChange = (productId, newQuantity) => {
    onQuantityChange?.(item.id, productId, newQuantity)
  }

  const handleConfirmClick = () => {
    // Collect current quantities from items
    const editedQuantities = {}
    item.items.forEach(lineItem => {
      editedQuantities[lineItem.productId] = lineItem.quantity
    })
    onConfirm?.(item, editedQuantities)
  }

  return (
    <div
      className={cn(
        'grid gap-4 px-6 py-3 text-sm border-b border-border/50 hover:bg-muted/30 transition-colors items-center',
        getRowBackground(),
        className
      )}
      style={{
        gridTemplateColumns: '9% 17% 1fr 12% 9%'
      }}
    >
      {/* Consignment Column */}
      <div>
        {getConsignmentDisplay()}
      </div>

      {/* Customer + Branch Column */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onCustomerClick?.(item.customer)}
            className="font-medium text-left hover:underline cursor-pointer"
          >
            {item.customer.name}
          </button>
          {item.marketEventId && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); window.location.href = `/sales/markets/${item.marketEventId}` }}
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors"
              title="Market sale"
            >
              <Store className="h-3 w-3" />
              Market
            </button>
          )}
        </div>
        {/* Hide branch if name is 'Main' (case-insensitive) */}
        {item.branch?.name && item.branch.name.toLowerCase() !== 'main' && (
          <span className="text-sm opacity-70">{item.branch.name}</span>
        )}
      </div>

      {/* Product Details — sub-grid: name | mass | grade | unit price | subtotal */}
      <div>
        {item.items.map((lineItem) => (
          <div
            key={lineItem.id}
            className="grid gap-x-1 items-center"
            style={{ gridTemplateColumns: '8rem 4.5rem 5.5rem 5.5rem 0.75rem 5rem' }}
          >
            <span className="whitespace-nowrap">
              {lineItem.itemNumber != null && (
                <span className="font-mono opacity-66 mr-1">{lineItem.itemNumber}</span>
              )}
              {lineItem.strain}
            </span>
            {lineItem.blockFormatId ? (
              <>
                <span className="text-muted-foreground" style={{ gridColumn: 'span 4' }}>{lineItem.quantity} x {lineItem.blockFormat?.name || 'Block'}</span>
                <span className="font-mono text-right">{formatCurrency(lineItem.subtotal)}</span>
              </>
            ) : lineItem.growingMaterialId ? (
              <>
                <span className="text-muted-foreground truncate" style={{ gridColumn: 'span 4' }}>
                  {lineItem.quantity} x {stripStrainPrefix(lineItem.growingMaterial?.name, lineItem.strain) || lineItem.growingMaterial?.unit_label || 'unit'}
                </span>
                <span className="font-mono text-right">{formatCurrency(lineItem.subtotal)}</span>
              </>
            ) : (
              <>
                <span className="text-right">
                  {isUnconfirmed ? (
                    <InlineQuantityInput
                      value={lineItem.quantity}
                      onChange={(newQuantity) => handleQuantityChange(lineItem.productId, newQuantity)}
                      disabled={isConfirming}
                    />
                  ) : isAdjustable ? (
                    <span className={cn(
                      (lineItem.adjusted || pendingChanges[lineItem.id]?.quantity != null) && 'ring-1 ring-amber-300/50 rounded px-1',
                      (pendingChanges[lineItem.id]?.quantity ?? lineItem.quantity) === 0 && 'line-through opacity-50'
                    )}>
                      <InlineQuantityInput
                        value={pendingChanges[lineItem.id]?.quantity ?? lineItem.quantity}
                        min={0}
                        onBlur={(newQuantity) => {
                          if (newQuantity !== lineItem.quantity) {
                            if (isInvoicedOrPaid) {
                              setPendingChanges(prev => ({
                                ...prev,
                                [lineItem.id]: { ...prev[lineItem.id], quantity: newQuantity }
                              }))
                            } else {
                              onAdjustmentChange?.({
                                saleItemId: lineItem.id,
                                sourceId: item.sourceId || item.id.replace('sale-', ''),
                                quantity: newQuantity
                              })
                            }
                          } else if (isInvoicedOrPaid && pendingChanges[lineItem.id]?.quantity != null) {
                            // User reverted to original — remove pending quantity change
                            setPendingChanges(prev => {
                              const updated = { ...prev }
                              const entry = { ...updated[lineItem.id] }
                              delete entry.quantity
                              if (Object.keys(entry).length === 0) delete updated[lineItem.id]
                              else updated[lineItem.id] = entry
                              return updated
                            })
                          }
                        }}
                      />
                      {lineItem.discountNote?.match(/^Adjusted\[.*qty.*\]/) && (
                        <span className="text-amber-400 ml-0.5" title={lineItem.discountNote.replace(/^Adjusted\[.*?\]:\s*/, '')}>*</span>
                      )}
                    </span>
                  ) : (
                    <span>
                      {lineItem.quantity}kg
                      {lineItem.discountNote?.match(/^Adjusted\[.*qty.*\]/) && (
                        <span className="text-amber-400 ml-0.5" title={lineItem.discountNote.replace(/^Adjusted\[.*?\]:\s*/, '')}>*</span>
                      )}
                    </span>
                  )}
                </span>
                <span className={cn(
                  'text-muted-foreground',
                  isAdjustable && (lineItem.adjusted || pendingChanges[lineItem.id]?.productTypeId != null) && !lineItem.blockFormatId && 'ring-1 ring-amber-300/50 rounded px-1'
                )}>
                  {isAdjustable && !lineItem.blockFormatId && productTypes.length > 0 ? (
                    <>
                      <InlineSelectField
                        options={productTypes.map(pt => ({ value: pt.id, label: pt.name }))}
                        value={pendingChanges[lineItem.id]?.productTypeId ?? lineItem.productTypeId}
                        onSave={(newProductTypeId) => {
                          if (newProductTypeId !== lineItem.productTypeId) {
                            if (isInvoicedOrPaid) {
                              setPendingChanges(prev => ({
                                ...prev,
                                [lineItem.id]: { ...prev[lineItem.id], productTypeId: newProductTypeId }
                              }))
                            } else {
                              onAdjustmentChange?.({
                                saleItemId: lineItem.id,
                                sourceId: item.sourceId || item.id.replace('sale-', ''),
                                productTypeId: newProductTypeId
                              })
                            }
                          } else if (isInvoicedOrPaid && pendingChanges[lineItem.id]?.productTypeId != null) {
                            // User reverted to original — remove pending class change
                            setPendingChanges(prev => {
                              const updated = { ...prev }
                              const entry = { ...updated[lineItem.id] }
                              delete entry.productTypeId
                              if (Object.keys(entry).length === 0) delete updated[lineItem.id]
                              else updated[lineItem.id] = entry
                              return updated
                            })
                          }
                        }}
                      />
                      {lineItem.discountNote?.match(/^Adjusted\[.*class.*\]/) && (
                        <span className="text-amber-400 ml-0.5" title={lineItem.discountNote.replace(/^Adjusted\[.*?\]:\s*/, '')}>*</span>
                      )}
                    </>
                  ) : (
                    <span>
                      {lineItem.productTypeName || ''}
                      {lineItem.discountNote?.match(/^Adjusted\[.*class.*\]/) && (
                        <span className="text-amber-400 ml-0.5" title={lineItem.discountNote.replace(/^Adjusted\[.*?\]:\s*/, '')}>*</span>
                      )}
                    </span>
                  )}
                </span>
                <span className="font-mono text-sm text-right opacity-66">{formatCurrency(lineItem.quantity > 0 ? lineItem.subtotal / lineItem.quantity : lineItem.price)}/kg</span>
                <span className="text-sm opacity-66">{/* Reserved for promotional asterisk */}
                  {lineItem.discountNote && !lineItem.discountNote.startsWith('Adjusted[') && (
                    <span title={lineItem.discountNote}>*</span>
                  )}
                </span>
                <span className="font-mono text-right">{formatCurrency(lineItem.subtotal)}</span>
              </>
            )}
          </div>
        ))}
        {hasPendingChanges && (
          <div className="flex justify-end gap-2 mt-2 pt-1 border-t border-white/20">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setPendingChanges({})}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-7 text-xs"
              onClick={() => {
                onAcceptAdjustments?.({
                  changes: pendingChanges,
                  item,
                  estimatedCredit
                })
                setPendingChanges({})
              }}
            >
              Accept
            </Button>
          </div>
        )}
      </div>

      {/* Status Column */}
      <div>
        {isUnconfirmed ? (
          <ConfirmButton
            onClick={handleConfirmClick}
            loading={isConfirming}
            disabled={isConfirming}
          />
        ) : (
          renderStatusSelect()
        )}
      </div>

      {/* Actions Column */}
      <div className="actions flex items-center gap-1">
        {isUnconfirmed ? (
          <>
            {/* Edit button for unconfirmed-recurring items */}
            {item.type === 'unconfirmed-recurring' && (
              <OdinButton
                variant="table-action"
                size="small"
                leftIcon={<EditIcon />}
                onClick={() => onEditRecurringOrder?.(item)}
                disabled={isConfirming}
                title="Edit recurring order"
              >
                Edit
              </OdinButton>
            )}
            {/* Dismiss button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDismiss?.(item)}
              disabled={isConfirming}
              className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
              title="Dismiss suggestion"
            >
              <X className="h-4 w-4" />
            </Button>
          </>
        ) : (
          renderActionButton()
        )}
      </div>
    </div>
  )
}
